import { useContext, useEffect, useState } from "react";
import { BiCheckCircle, BiLink, BiPin } from "react-icons/bi";
import UserContext from "../User/UserContext";
import LoadingCircle from "../utility/LoadingCircle";
import { FaCircle, FaHourglassHalf, FaInfoCircle, FaLock, FaShoppingCart, FaStop, FaUnlock, FaUser } from "react-icons/fa";

export default function GeneratePublicLinkButton({ projectId }) {
	const [showModal, setShowModal] = useState(false);
	const [showRejectInfo, setShowRejectInfo] = useState(false);
	const [rejectInfo, setRejectInfo] = useState(null);
	const [loading, setLoading] = useState(false);
	const [links, setLinks] = useState([]);
	const [hasCustomerLink, setHasCustomerLink] = useState(true);
	const { api, token } = useContext(UserContext);

	useEffect(() => {
		if (!api || !token) {
			return;
		}
		setLoading(true);
		fetch(api + "/public/link/" + projectId, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		})
			.then((res) => {
				if (res.ok) {
					return res.json();
				}
			})
			.then((data) => {
				if (data) {
					setLinks(data.links);
					setHasCustomerLink(data.links.some((link) => link.for_customer_portal === 1));
				}
			})
			.finally(() => setLoading(false));
	}, [api, projectId, token]);

	const generateCustomerPortalLink = () => {
		setLoading(true);
		fetch(api + "/public/get-customer-link/" + projectId, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		})
			.then((res) => {
				if (res.ok) {
					return res.json();
				} else {
					alert("an error occurred while getting public links");
				}
			})
			.then((data) => {
				if (data) {
					setLinks((prev) => {
						return [data.link, ...prev];
					});
					setHasCustomerLink(true);
				} else {
					alert("Unable to create customer portal link manually.");
				}
			})
			.finally(() => setLoading(false));
	};

	const generatePublicLink = () => {
		setLoading(true);
		fetch(api + "/public/generate-link/" + projectId, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		})
			.then((res) => {
				if (res.ok) {
					return res.json();
				} else {
					alert("an error occurred while getting public links");
				}
			})
			.then((data) => {
				if (data) {
					setLinks((prev) => {
						const newLinks = [data.link, ...prev];
						return newLinks.sort((a, b) => b.for_customer_portal - a.for_customer_portal);
					});
				}
			})
			.finally(() => setLoading(false));
	};

	const [showCopiedToast, setShowCopiedToast] = useState(false);
	const handleCopyLink = (publicLink) => {
		navigator.clipboard.writeText(publicLink).then(() => {
			setShowCopiedToast(true);
			setTimeout(() => setShowCopiedToast(false), 2000);
		});
	};

	const openRejectInfo = (link) => {
		setRejectInfo(link);
		setShowRejectInfo(true);
	};

	const getRejecterName = (link) => link?.rejecter_name || link?.rejected_by || link?.rejecter || "-";
	const getRejectReason = (link) => link?.reject_reason || link?.rejected_reason || link?.reason || "-";
	const getRejectedAt = (link) => link?.rejected_at || link?.rejected_on || link?.rejected_time || null;
	const formatRejectedAt = (value) => {
		if (!value) {
			return "-";
		}
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
	};

	const toggleActive = (linkId, newState) => {
		setLoading(true);
		fetch(api + "/public/toggle-link/" + linkId, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({ value: newState }),
		})
			.then((res) => {
				if (res.ok) {
					setLinks((prevLinks) => prevLinks.map((link) => (link.id === linkId ? { ...link, is_active: newState } : link)));
				} else {
					alert("an error occurred while toggling the state");
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const togglePermanent = (linkId, newState) => {
		setLoading(true);
		fetch(api + "/public/toggle-permanent/" + linkId, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({ value: newState }),
		})
			.then((res) => {
				if (res.ok) {
					setLinks((prevLinks) =>
						prevLinks.map((link) =>
							link.id === linkId
								? {
										...link,
										is_permanent: newState,
										expires_at: newState === false ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : link.expires_at,
								  }
								: link
						)
					);
				} else {
					alert("an error occurred while toggling the state");
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const toggleShop = (linkId, newValue) => {
		setLoading(true);
		fetch(api + "/public/toggle-shop/" + linkId, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify( {value : newValue }),
		})
			.then(res => {
				if (res.ok) {
					return res.json();
				}
			})
			.then(data => {
				if (data && (data.shopEnabled === 0 || data.shopEnabled === 1)) {
					setLinks((prevLinks) =>
						prevLinks.map((link) =>
							link.id === linkId
								? { ...link, shop_enabled: data.shopEnabled }
								: link
						)
					);
				}
			})
			.finally(() => setLoading(false));
	}

	const addPasscode = (linkId) => {
		setLoading(true);
		fetch(api + "/public/passcode-add/" + linkId, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		})
			.then((res) => {
				if (res.ok) {
					return res.json();
				} else {
					alert("an error occurred while toggling the state");
				}
			})
			.then((data) => {
				if (data) {
					setLinks((prevLinks) =>
						prevLinks.map((link) =>
							link.id === linkId
								? {
										...link,
										passcode: data.newPasscode,
								  }
								: link
						)
					);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const removePasscode = (linkId) => {
		setLoading(true);
		fetch(api + "/public/passcode-remove/" + linkId, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
		})
			.then((res) => {
				if (res.ok) {
					setLinks((prevLinks) =>
						prevLinks.map((link) =>
							link.id === linkId
								? {
										...link,
										passcode: null,
								  }
								: link
						)
					);
				} else {
					alert("an error occurred while toggling the state");
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	if (loading && showModal) {
		return (
			<div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-50 z-[2000] flex justify-center items-center">
				<div className="bg-white text-black p-4 rounded-md shadow-lg flex justify-center items-center flex-col">
					<LoadingCircle />
					<p>Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<>
			{showCopiedToast && (
				<div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-slide-up-down z-[2001]">
					Link copied!
				</div>
			)}

			{showModal ? (
				<div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-50 z-[2000] flex justify-center items-center">
					<div className="bg-white text-black p-4 rounded-md shadow-lg w-full md:w-4/5">
						<div className="flex justify-between items-center mb-6">
							<div>
								<p className="text-2xl font-bold ">Public Links</p>
								<p>Links from the last 60 days</p>
							</div>
							<div className="flex gap-2">
								{hasCustomerLink ? null : (
									<button
										className="bg-blue-500 hover:bg-blue-600 p-2 text-white rounded-md flex items-center gap-1"
										onClick={generateCustomerPortalLink}>
										<FaUser />
										Generate Customer Portal Link
									</button>
								)}
								<button
									className="bg-blue-500 hover:bg-blue-600 p-2 text-white rounded-md flex items-center gap-1"
									onClick={generatePublicLink}>
									<BiLink />
									Generate New Public Link
								</button>
							</div>
						</div>
						{links && links.length > 0 ? (
							<div className="select-text h-[60vh] overflow-y-scroll">
								<table className="min-w-full table-auto border-collapse border border-gray-300">
									<thead>
										<tr>
											<th className="border border-gray-300 px-4 py-2 text-left">Public Link</th>
											<th className="border border-gray-300 px-4 py-2 text-left">Passcode</th>
											<th className="border border-gray-300 px-4 py-2 text-left">Expires In</th>
											<th className="border border-gray-300 px-4 py-2 text-left">Created By</th>
											<th className="border border-gray-300 px-4 py-2 text-left">Status</th>
											<th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
										</tr>
									</thead>
									<tbody>
										{links.map((link) => {
											const publicLink = `${window.location.origin}/public/project/${link.project_id}?token=${link.hash}`;
											const localExpiresAt = new Date(link.expires_at).toLocaleString();
											const isExpired = new Date(link.expires_at) < new Date();
											const expiresInMs = new Date(link.expires_at) - new Date();
											const expiresInDays = Math.floor(expiresInMs / (1000 * 60 * 60 * 24));
											const expiresInHours = Math.floor(expiresInMs / (1000 * 60 * 60));

											return (
												<tr key={link.id} className={link.for_customer_portal ? "bg-gray-200" : ""}>
													<td className="border border-gray-300 px-4 py-2">
														<button className="text-blue-600 underline" onClick={() => handleCopyLink(publicLink)}>
															Get Link
														</button>
														<p>{link.hash}</p>
													</td>
													<td className="border border-gray-300 px-4 py-2">
														<p>{link.passcode}</p>
													</td>
													<td className="border border-gray-300 px-4 py-2">
														<p className={`mb-1 ${link.is_permanent ? "text-blue-500 " : ""}`}>
															{link.is_permanent
																? "PERMANENT"
																: expiresInMs <= 0
																? "Expired"
																: expiresInDays >= 1
																? `${expiresInDays} day${expiresInDays > 1 ? "s" : ""}`
																: `${expiresInHours} hour${expiresInHours > 1 ? "s" : ""}`}
														</p>
													</td>
													<td className="border border-gray-300 px-4 py-2">{link.created_by}</td>
													<td className="border border-gray-300 px-4 py-2">
														{link.is_rejected ? (
															<div className="flex flex-col items-start gap-2">
																<span className="px-2 py-1 text-red-600 text-lg rounded-md">Rejected</span>
																<button
																	type="button"
																	onClick={() => openRejectInfo(link)}
																	className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-md flex items-center gap-2 text-sm"
																	title="View rejection details">
																	<FaInfoCircle />
																	<span>View details</span>
																</button>
															</div>
														) : link.is_approved ? (
															<span className="px-2 py-1 text-green-500 text-lg rounded-md">Approved</span>
														) : isExpired ? (
															<span className="px-2 py-1 text-gray-500 text-lg rounded-md">Expired</span>
														) : link.is_active ? (
															<span className="px-2 py-1 text-blue-500 text-lg rounded-md">Enabled</span>
														) : (
															<span className="px-2 py-1 text-red-500 text-lg rounded-md">Disabled</span>
														)}
													</td>
													<td className="border border-gray-300 px-4 py-2 ">
														{link.for_customer_portal ? (
															<div className="flex justify-center items-center">
																<FaLock />
															</div>
														) : (
															<div className="flex flex-col gap-2 h-full">
																{link.passcode ? (
																	<button
																		onClick={() => {
																			removePasscode(link.id);
																		}}
																		className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<FaUnlock />
																		Remove Passcode
																	</button>
																) : (
																	<button
																		onClick={() => {
																			addPasscode(link.id);
																		}}
																		className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<FaLock />
																		Require Passcode
																	</button>
																)}
																{link.is_permanent ? (
																	<button
																		onClick={() => {
																			togglePermanent(link.id, !link.is_permanent);
																		}}
																		className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<FaHourglassHalf />
																		Make Temporary
																	</button>
																) : (
																	<button
																		onClick={() => {
																			togglePermanent(link.id, !link.is_permanent);
																		}}
																		className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<BiPin />
																		Make Permanent
																	</button>
																)}

																{link.shop_enabled ? (
																	<button
																		onClick={() => {
																			toggleShop(link.id, 0);
																		}}
																		className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<FaLock />
																		Disable Shop
																	</button>
																) : (
																	<button
																		onClick={() => {
																			toggleShop(link.id, 1);
																		}}
																		className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-md flex items-center justify-center gap-2">
																		<FaShoppingCart />
																		Enable Shop
																	</button>
																)}

																{!link.is_approved && !isExpired ? (
																	<button
																		className={`px-2 py-1 text-white ${
																			link.is_active ? "bg-red-500 hover:bg-red-600 " : "bg-blue-500 hover:bg-blue-600"
																		} rounded-md flex items-center justify-center gap-2`}
																		onClick={() => {
																			toggleActive(link.id, !link.is_active);
																		}}>
																		{link.is_active ? <FaStop /> : <BiCheckCircle />}
																		{link.is_active ? "Disable Link" : "Enable Link"}
																	</button>
																) : null}
															</div>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						) : (
							<div className="border border-black p-4 rounded-md h-[60vh] flex justify-center items-center">
								<p className="text-center">This site currently does not have any links</p>
							</div>
						)}
						<div className="mt-5 flex flex justify-end gap-2">
							<button onClick={() => setShowModal(false)} className="bg-gray-500 hover:bg-gray-600 text-white p-2  rounded-md">
								Close
							</button>
						</div>
					</div>
				</div>
			) : null}
			{showRejectInfo ? (
				<div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-50 z-[2000] flex justify-center items-center">
					<div className="bg-white text-black rounded-lg shadow-xl w-full md:w-2/5">
						<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
							<div>
								<p className="text-2xl font-bold">Rejection Details</p>
							</div>
						</div>
						<div className="px-6 py-5 ">
							<div className="rounded-md p-3">
								<p className="text-xs uppercase tracking-wide text-gray-500">Rejected By</p>
								<p className="text-base">
									{getRejecterName(rejectInfo)} @ {formatRejectedAt(getRejectedAt(rejectInfo))}
								</p>
							</div>
							<div className="rounded-md p-3">
								<p className="text-xs uppercase tracking-wide text-gray-500">Message</p>
								<div className="border border-gray-200 bg-gray-50 rounded-md p-3 mt-2">
									<p className="text-base whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
										{getRejectReason(rejectInfo)}
									</p>
								</div>
							</div>
						</div>
						<div className="px-6 pb-5">
							<button
								onClick={() => {
									setShowRejectInfo(false);
									setRejectInfo(null);
								}}
								className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md w-full">
								Close
							</button>
						</div>
					</div>
				</div>
			) : null}

			<div
				className="flex items-center gap-1  bg-blue-600 hover:bg-blue-700 p-2 rounded-lg w-full cursor-pointer select-none"
				onClick={() => setShowModal(true)}>
				<BiLink />
				<p>Generate Link</p>
			</div>
		</>
	);
}
